import React, { useEffect, useState } from "react";
import { Card, CardBody, Container, Row, Col, CardTitle, Table, Button } from "reactstrap";
import Spinner from "../../common/spinner/spinner";

interface SignatureRequest {
  id: string;
  clientFullName: string;
  ccaStatus: 'Signed' | 'Pending' | 'Not Required';
  treatmentPlanStatus: 'Signed' | 'Pending' | 'Not Required';
  treatmentPlanId?: string;
  clientId: string;
}

const MedicalDirectorDashboard: React.FC = () => {
  const [signatureRequests, setSignatureRequests] = useState<SignatureRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchSignatureRequests();
  }, []);

  const fetchSignatureRequests = () => {
    // TODO: Replace with actual API call to get pending signature requests
    // For now, using mock data
    const mockData: SignatureRequest[] = [
      {
        id: '1',
        clientFullName: 'John Doe',
        ccaStatus: 'Signed',
        treatmentPlanStatus: 'Pending',
        treatmentPlanId: 'tp_001',
        clientId: 'client_001'
      },
      {
        id: '2',
        clientFullName: 'Jane Smith',
        ccaStatus: 'Signed',
        treatmentPlanStatus: 'Pending',
        treatmentPlanId: 'tp_002',
        clientId: 'client_002'
      },
      {
        id: '3',
        clientFullName: 'Mike Johnson',
        ccaStatus: 'Pending',
        treatmentPlanStatus: 'Not Required',
        clientId: 'client_003'
      }
    ];

    setSignatureRequests(mockData);
    setIsLoading(false);
  };

  const handleViewDocument = (treatmentPlanId: string) => {
    // TODO: Implement view document functionality
    console.log('View document for treatment plan:', treatmentPlanId);
  };

  const handleEditSignature = (treatmentPlanId: string) => {
    // TODO: Implement signature pad modal for MD signature
    console.log('Edit MD signature for treatment plan:', treatmentPlanId);
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col xl="12">
              <Card>
                <CardBody>
                  <CardTitle className="mb-4">
                    <h4>Clinical Signatures</h4>
                    <p className="text-muted">Manage pending signature requests for client consent agreements and treatment plans</p>
                  </CardTitle>

                  {isLoading ? (
                    <div className="text-center">
                      <Spinner />
                    </div>
                  ) : (
                    <Table responsive className="table-nowrap">
                      <thead className="table-light">
                        <tr>
                          <th>Client Full Name</th>
                          <th>CCA Status</th>
                          <th>Treatment Plan Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {signatureRequests.map((request) => (
                          <tr key={request.id}>
                            <td>
                              <h5 className="font-size-14 mb-1">{request.clientFullName}</h5>
                            </td>
                            <td>
                              <span className={`badge ${
                                request.ccaStatus === 'Signed' ? 'bg-success' :
                                request.ccaStatus === 'Pending' ? 'bg-warning' : 'bg-secondary'
                              }`}>
                                {request.ccaStatus}
                              </span>
                            </td>
                            <td>
                              <span className={`badge ${
                                request.treatmentPlanStatus === 'Signed' ? 'bg-success' :
                                request.treatmentPlanStatus === 'Pending' ? 'bg-warning' : 'bg-secondary'
                              }`}>
                                {request.treatmentPlanStatus}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex gap-2">
                                {request.treatmentPlanId && (
                                  <>
                                    <Button
                                      color="info"
                                      size="sm"
                                      onClick={() => handleViewDocument(request.treatmentPlanId!)}
                                    >
                                      View Document
                                    </Button>
                                    {request.treatmentPlanStatus === 'Pending' && (
                                      <Button
                                        color="primary"
                                        size="sm"
                                        onClick={() => handleEditSignature(request.treatmentPlanId!)}
                                      >
                                        MD Signature
                                      </Button>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}

                  {!isLoading && signatureRequests.length === 0 && (
                    <div className="text-center py-4">
                      <p className="text-muted">No pending signature requests found.</p>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default MedicalDirectorDashboard;
